/**
 * Consolidated Session Management
 * Simplified implementation using react-oauth2-code-pkce storage keys
 * Token refresh is handled by react-oauth2-code-pkce library
 */

// Session configuration interface
export interface SessionConfig {
  accessTokenExpiry: number; // in seconds
  refreshTokenExpiry: number; // in seconds
  idleTimeout: number; // in seconds
  warningModalTime: number; // in seconds
  tokenExpiryBufferSeconds: number; // in seconds - buffer before actual expiry to consider token expired
  isTestMode: boolean;
}

/**
 * Get session configuration based on environment
 */
/**
 * Session configuration object
 */
export const getSessionConfig = (): SessionConfig => {
  // Use globalThis to access environment variables in a way that works in both Vite and Jest
  const isTestMode =
    (globalThis as { import?: { meta?: { env?: Record<string, string> } } })
      .import?.meta?.env?.VITE_TEST_SESSION_TIMEOUT === "true";

  let config: SessionConfig;

  if (isTestMode) {
    config = {
      accessTokenExpiry: 240, // 4 minutes
      refreshTokenExpiry: 1800, // 30 minutes
      idleTimeout: 120, // 2 minutes
      warningModalTime: 60, // 1 minute
      tokenExpiryBufferSeconds: 10, // 10 seconds buffer for test mode (small buffer for short-lived tokens)
      isTestMode: true,
    };
  } else {
    config = {
      accessTokenExpiry: 600, // 10 minutes
      refreshTokenExpiry: 3000, // 50 minutes
      idleTimeout: 1200, // 20 minutes
      warningModalTime: 300, // 5 minutes
      tokenExpiryBufferSeconds: 300, // 5 minutes buffer for production mode (300 seconds)
      isTestMode: false,
    };
  }

  return config;
};

/**
 * Check if token is expired (with configurable buffer)
 */
export const isTokenExpired = (
  expiresAt: number,
  bufferSeconds?: number,
): boolean => {
  const now = Math.floor(Date.now() / 1000); // Current time in seconds
  const config = getSessionConfig();
  const buffer = bufferSeconds || config.tokenExpiryBufferSeconds;
  return now >= expiresAt - buffer;
};

/**
 * Get token from react-oauth2-code-pkce storage
 */
export const getStoredToken = (): string | null => {
  try {
    const token = sessionStorage.getItem("ROCP_token");
    if (!token) return null;
    return JSON.parse(token) as string;
  } catch (error) {
    console.warn("Failed to get stored token:", error);
    return null;
  }
};

/**
 * Get token expiry from react-oauth2-code-pkce storage
 */
export const getStoredTokenExpiry = (): number | null => {
  try {
    const expiry = sessionStorage.getItem("ROCP_tokenExpire");
    if (!expiry) return null;
    return parseInt(expiry, 10);
  } catch (error) {
    console.warn("Failed to get stored token expiry:", error);
    return null;
  }
};

/**
 * Get refresh token from react-oauth2-code-pkce storage (read-only)
 */
export const getStoredRefreshToken = (): string | null => {
  try {
    const refreshToken = sessionStorage.getItem("ROCP_refreshToken");
    if (!refreshToken) return null;
    return JSON.parse(refreshToken) as string;
  } catch (error) {
    console.warn("Failed to get stored refresh token:", error);
    return null;
  }
};

/**
 * Synchronous version of session validation (no refresh attempt)
 */
export const isSessionValidSync = (): boolean => {
  const token = getStoredToken();
  const expiry = getStoredTokenExpiry();

  if (!token || !expiry) return false;
  return !isTokenExpired(expiry);
};

/**
 * Clear all stored tokens from sessionStorage
 */
export const clearStoredTokens = (): void => {
  try {
    const keysToRemove = [
      "ROCP_token",
      "ROCP_tokenExpire",
      "ROCP_refreshToken",
      "ROCP_idToken",
      "ROCP_state",
      "ROCP_verifier",
    ];

    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  } catch (error) {
    console.warn("Failed to clear stored tokens:", error);
  }
};

/**
 * Clear a stale `loginInProgress` flag left in sessionStorage by an aborted or
 * interrupted OAuth redirect.
 *
 * react-oauth2-code-pkce persists `ROCP_loginInProgress` in sessionStorage. On
 * mount, if that flag is `true` but the URL has no `?code=` parameter, the
 * library throws "Expected to find a '?code=' parameter in the URL by now...".
 * In non-incognito mode sessionStorage survives reloads/session-restore, so a
 * stale flag from a previous interrupted login makes the app error out as soon
 * as the root URL is opened.
 *
 * We only reset the flag when the current URL is NOT a genuine OAuth callback
 * (no `?code=` and no `?error=`), so real callbacks and provider-reported
 * errors are still handled by the library.
 *
 * Must run before <AuthProvider> mounts.
 */
export const clearStaleLoginInProgress = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);

    // Genuine OAuth callback (success or provider error) — let the library handle it.
    if (params.has("code") || params.has("error")) return;

    const flag = sessionStorage.getItem("ROCP_loginInProgress");
    if (flag && flag !== "false") {
      sessionStorage.setItem("ROCP_loginInProgress", JSON.stringify(false));
    }
  } catch (error) {
    console.warn("Failed to clear stale loginInProgress flag:", error);
  }
};
